import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { Radio, Calendar, Lock, ExternalLink, Youtube, Twitch } from "lucide-react";

const PLATFORMS = {
  youtube: { label: "YouTube Live", icon: Youtube, color: "text-red-500" },
  twitch: { label: "Twitch", icon: Twitch, color: "text-purple-400" },
  instagram: { label: "Instagram Live", icon: Radio, color: "text-pink-400" },
  tiktok: { label: "TikTok Live", icon: Radio, color: "text-cyan-400" },
  zoom: { label: "Zoom", icon: Radio, color: "text-blue-400" },
  other: { label: "Live", icon: Radio, color: "text-pfc-gold" },
};

export default function LiveNow() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Live Now — PFC";
    api.get("/live").then((r) => { setRows(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const live = rows.filter((r) => r.status === "live");
  const upcoming = rows.filter((r) => r.status === "upcoming");

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="live-page">
      <div className="relative border-b border-white/10 bg-gradient-to-b from-black via-ink-900 to-ink-900">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="flex items-center gap-2 text-pfc-gold font-sub uppercase tracking-[0.35em] text-xs mb-4">
            <Radio size={14} className="animate-pulse" /> Live Now · Exclusive Appearances
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
            The room is open. The mic is hot.
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl text-base">
            Tune into livestreams, Q&amp;As, and exclusive performances from PFC artists, athletes, founders, and creators.
            <span className="text-pfc-gold"> Premium members</span> unlock locked rooms and Premium creators can publish their own.
          </p>
          <div className="mt-7">
            <Link
              to="/settings?tab=live"
              className="inline-flex items-center gap-2 px-5 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-xs hover:bg-pfc-gold-bright transition"
              data-testid="post-live-cta"
            >
              <Radio size={14} /> Schedule Your Live
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
        {loading ? (
          <div className="text-white/40 py-20 text-center">Loading…</div>
        ) : (
          <>
            {/* LIVE NOW row */}
            <Section title="🔴 Live Now" testId="live-now-section">
              {live.length === 0 && <Empty>No active live streams right now.</Empty>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="live-now-grid">
                {live.map((r) => <LiveCard key={r.live_id} row={r} />)}
              </div>
            </Section>

            <Section title="🗓 Upcoming" testId="upcoming-section">
              {upcoming.length === 0 && <Empty>No upcoming live appearances yet.</Empty>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="upcoming-grid">
                {upcoming.map((r) => <LiveCard key={r.live_id} row={r} />)}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <div className="mb-14" data-testid={testId}>
      <h2 className="font-display text-2xl mb-6 tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-white/40 text-sm py-8 border border-dashed border-white/10 text-center">{children}</div>;
}

function LiveCard({ row }) {
  const platformInfo = PLATFORMS[row.platform] || PLATFORMS.other;
  const PIcon = platformInfo.icon;
  const isLocked = row.locked;
  const startStr = new Date(row.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div className="border border-white/10 bg-ink-800/40 overflow-hidden hover:border-pfc-gold transition" data-testid={`live-card-${row.live_id}`}>
      <div className="aspect-video bg-black/50 relative overflow-hidden">
        {row.cover_image ? (
          <img src={row.cover_image} alt={row.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-white/20">
            <PIcon size={48} />
          </div>
        )}
        {row.status === "live" && (
          <div className="absolute top-3 left-3 bg-pfc-red text-white text-[10px] uppercase tracking-[0.3em] font-sub px-2 py-1 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live
          </div>
        )}
        <div className={`absolute top-3 right-3 ${platformInfo.color} bg-black/70 px-2 py-1 text-[10px] uppercase tracking-[0.3em] font-sub inline-flex items-center gap-1`}>
          <PIcon size={11} /> {platformInfo.label}
        </div>
        {isLocked && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm grid place-items-center">
            <div className="text-center">
              <Lock className="mx-auto text-pfc-gold mb-2" />
              <div className="text-xs uppercase tracking-[0.3em] font-sub text-pfc-gold">Premium Exclusive</div>
              <Link to="/settings?tab=premium" className="text-white/70 text-xs mt-2 inline-block hover:text-white">Upgrade to unlock →</Link>
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-lg leading-tight">{row.title}</h3>
        {row.user && (
          <Link to={row.user.profile_id ? `/profile/${row.user.profile_id}` : "#"} className="text-pfc-gold text-[11px] tracking-[0.3em] uppercase font-sub mt-2 inline-block hover:underline" data-testid={`live-by-${row.live_id}`}>
            By {row.user.name || "Creator"}
          </Link>
        )}
        <div className="text-white/60 text-xs mt-3 inline-flex items-center gap-1">
          <Calendar size={11} /> {startStr}
        </div>
        {row.description && <p className="text-white/70 text-sm mt-3 line-clamp-3">{row.description}</p>}
        {!isLocked && row.stream_url && (
          <a
            href={row.stream_url}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-[11px] hover:bg-pfc-gold-bright transition"
            data-testid={`watch-${row.live_id}`}
          >
            <ExternalLink size={11} /> {row.status === "live" ? "Watch Now" : "Open Link"}
          </a>
        )}
      </div>
    </div>
  );
}
